import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Alert
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import AccessCodeServiceInstance from '../../services/AccessCodeService';

export default function AccessCode() {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation('common');

    // Get the return URL from query params or default to home
    const returnUrl = searchParams.get('returnUrl') || '/';

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!code.trim()) {
            setError(t('accessCode.errorEmpty', 'Please enter an access code') as string);
            return;
        }

        // Store the access code in session storage
        AccessCodeServiceInstance.setAccessCode(code.trim());

        // Navigate back to the original page
        navigate(returnUrl, { replace: true });
    };

    const handleCancel = () => {
        navigate('/', { replace: true });
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: 'warning.main',
                            borderRadius: '50%',
                            padding: 2,
                            marginBottom: 2
                        }}
                    >
                        <LockIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>

                    <Typography component="h1" variant="h5" gutterBottom>
                        {t('accessCode.title', 'Access Restricted')}
                    </Typography>

                    <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
                        {t('accessCode.description', 'This content requires an access code. Please enter the code provided to you.')}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label={t('accessCode.label', 'Access Code')}
                            variant="outlined"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            autoFocus
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                type="button"
                                variant="outlined"
                                fullWidth
                                onClick={handleCancel}
                            >
                                {t('accessCode.cancel', 'Cancel')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                color="primary"
                            >
                                {t('accessCode.submit', 'Submit')}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}
